import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowRight, Clock } from "lucide-react";
import { api, useResource, COLORS, fmtMoney } from "@/lib/api";
import { UpNextFolders } from "@/components/dashboard/UpNextFolders";
import { celebrateIfDone } from "@/lib/celebrate";
import { cardCls, CheckCircle } from "@/components/shared";
import { NoteCard } from "@/pages/Notes";
import { TaskReminders } from "@/components/TaskReminders";
import { PriceDrops } from "@/components/shop/PriceDrops";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { DailyQuote } from "@/components/DailyQuote";

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const Widget = ({ title, to, testId, className = "", children }) => (
  <div className={`${cardCls} p-6 fade-up ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <h2 className="font-serif-display text-2xl font-semibold">{title}</h2>
      <Link to={to} data-testid={`${testId}-view-all`} className="text-xs font-medium text-t-primary hover:text-t-primary-hover flex items-center gap-1">
        View all <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
    {children}
  </div>
);

const Empty = ({ children }) => <p className="text-sm text-t-faint py-4">{children}</p>;

export default function Dashboard() {
  const todos = useResource("/todo-lists");
  const shops = useResource("/shop-lists");
  const events = useResource("/events");
  const notes = useResource("/notes");
  const { theme } = useTheme();
  const { user } = useAuth();
  const today = format(new Date(), "yyyy-MM-dd");

  const upcoming = events.data.filter((e) => e.date >= today).slice(0, 5);
  const toBuy = shops.data.filter((s) => s.kind === "tobuy").flatMap((s) => s.items.filter((i) => !i.bought).map((i) => ({ ...i, shop: s })));
  const pinned = notes.data.filter((n) => n.pinned).slice(0, 3);
  const totalOpen = todos.data.reduce((s, l) => s + l.items.filter((i) => !i.done).length, 0);

  const toggleTask = async (t) => {
    const updated = (await api.patch(`/todo-lists/${t.list.id}/items/${t.id}`, { done: true })).data;
    todos.replace(updated);
    celebrateIfDone(t.list, updated);
  };
  const toggleBuy = async (i) => shops.replace((await api.patch(`/shop-lists/${i.shop.id}/items/${i.id}`, { bought: true })).data);

  return (
    <div data-testid="dashboard-page">
      <div className="relative overflow-hidden rounded-3xl mb-10 fade-up" data-testid="dashboard-hero">
        <img src={theme.hero} alt="Calm scene" data-testid="dashboard-hero-image" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-transparent" />
        <div className="relative px-8 sm:px-12 py-12 sm:py-16 text-white max-w-2xl">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-t-primary-light mb-3">{format(new Date(), "EEEE, MMMM d")}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-none" data-testid="dashboard-greeting">{greeting()}.</h1>
          <p className="text-sm md:text-base text-white/80 mt-4">
            You have <span className="font-semibold text-white" data-testid="dashboard-open-count">{totalOpen}</span> open tasks,{" "}
            <span className="font-semibold text-white">{upcoming.length}</span> upcoming events and{" "}
            <span className="font-semibold text-white">{toBuy.length}</span> things to buy.
          </p>
          <DailyQuote />
        </div>
      </div>

      {user?.prefs?.app_task_reminders !== false && <TaskReminders todos={todos.data} onToggle={toggleTask} />}
      {user?.prefs?.app_price_drops !== false && <PriceDrops shops={shops.data} />}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <Widget title="Up next" to="/todos" testId="widget-tasks" className="md:col-span-7">
          <UpNextFolders lists={todos.data} onTick={toggleTask} />
        </Widget>

        <Widget title="Upcoming" to="/calendar" testId="widget-events" className="md:col-span-5">
          {upcoming.length ? (
            <ul className="space-y-3">
              {upcoming.map((e) => (
                <li key={e.id} className="flex gap-4 items-center" data-testid={`dash-event-${e.id}`}>
                  <div className="w-12 text-center rounded-xl py-1.5 text-white shrink-0" style={{ backgroundColor: COLORS[e.color] }}>
                    <p className="text-[10px] uppercase">{format(parseISO(e.date), "MMM")}</p>
                    <p className="font-serif-display text-xl font-semibold leading-none">{format(parseISO(e.date), "d")}</p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{e.title}</p>
                    {e.time && <p className="text-xs text-t-muted flex items-center gap-1"><Clock className="h-3 w-3" />{e.time}</p>}
                  </div>
                </li>
              ))}
            </ul>
          ) : <Empty>No upcoming events.</Empty>}
        </Widget>

        <Widget title="To buy" to="/lists" testId="widget-tobuy" className="md:col-span-5">
          {toBuy.length ? (
            <ul className="divide-y divide-t-surface">
              {toBuy.slice(0, 6).map((i) => (
                <li key={i.id} className="flex items-center gap-3 py-3" data-testid={`dash-buy-${i.id}`}>
                  <CheckCircle checked={false} onClick={() => toggleBuy(i)} color="rgb(var(--t-c2))" testId={`dash-buy-check-${i.id}`} />
                  <span className="flex-1 text-sm">{i.name}</span>
                  {i.price != null && <span className="text-sm font-medium">{fmtMoney(i.price)}</span>}
                </li>
              ))}
            </ul>
          ) : <Empty>Your to-buy lists are empty.</Empty>}
        </Widget>

        <Widget title="Pinned notes" to="/notes" testId="widget-notes" className="md:col-span-7">
          {pinned.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {pinned.map((n) => <Link key={n.id} to="/notes"><NoteCard note={n} onOpen={() => {}} /></Link>)}
            </div>
          ) : <Empty>Pin a note to keep it close.</Empty>}
        </Widget>
      </div>
    </div>
  );
}
